/**
 * Ag-Grid 인라인 편집 페이지별 설정
 *
 * AgGrid 컴포넌트는 이 설정만 보고 동작한다(페이지 이름을 직접 분기하지 않는다).
 * 새 페이지를 추가할 때는 여기에 항목 하나만 추가하면 된다.
 *
 * idField      : 행 식별자 필드명 (getRowId / dirty 추적 키)
 * actionsColId : 행 저장/취소 버튼 컬럼의 colId (refreshCells 대상)
 * save         : (id, dto) => Promise — 수정 API
 * loadFull     : (id) => Promise<row> — 저장 직전 원본 조회(선택).
 *                수정 API 가 전체 갱신이라 목록에 없는 필드가 지워지는 경우에만 쓴다.
 *                지정하면 조회 실패 시 저장을 중단한다.
 * toDTO        : (row, full) => 요청 바디 (표시 전용 필드 제거·형 변환)
 * applyEditing : colDefs => colDefs (editable/cellEditor/valueSetter 주입)
 * validateField: (key, value, row) => '' | '에러메시지'
 * rowChecks    : [(row, api, changedFields) => '' | '에러메시지'] — 행 단위 추가 검증(중복 등)
 */
import { editProgram, getProgramDetail, createProgram } from 'api/program.api'
import { editTutor, getTutorDetail, createTutor } from 'api/tutor.api'
import { validateProgramField, validateProgramRow, PROGRAM_OPTIONS } from 'utils/validation/programEditSpec'
import { validateTutorField, validateTutorRow, TUTOR_OPTIONS } from 'utils/validation/tutorEditSpec'
import { applyProgramEditing, mapProgramRowToEditDTO } from './programEditColumns'
import { applyTutorEditing, mapTutorRowToEditDTO } from './tutorEditColumns'

const todayStr = () => {
    const d = new Date()
    const pad = n => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * 같은 지역·활동연도 내 동명이인 검사.
 * 서버 /valid 는 "로그인 사용자 지역"으로만 조회해 마스터가 타 지역을 편집할 때 무력하므로,
 * 그리드에 로드된 데이터 기준으로 클라이언트에서 검사한다.
 * 이름을 실제로 바꾼 행에만 적용.
 */
const checkTutorDuplicateName = (row, api, changedFields) => {
    if (!changedFields || !changedFields.has('tutorName')) return ''
    const name = String(row.tutorName ?? '').trim()
    if (!name) return ''

    let dup = false
    api?.forEachNode(n => {
        const d = n.data
        if (!d || d.tutorId === row.tutorId) return // 자기 자신 제외
        if (
            String(d.tutorName ?? '').trim() === name &&
            d.registrationYear === row.registrationYear &&
            d.regionId === row.regionId
        ) {
            dup = true
        }
    })
    return dup ? `동일한 강사명이 존재합니다: ${name}` : ''
}

export const GRID_EDIT_CONFIG = {
    program: {
        label: '교육실적',
        idField: 'programId',
        actionsColId: 'programEditActions',
        save: editProgram,
        // 목록에는 없는 강사 매칭(tutors) 등이 전체 갱신에 지워지지 않도록 상세를 바탕으로 깐다
        loadFull: async id => (await getProgramDetail(id))?.data?.data,
        toDTO: mapProgramRowToEditDTO,
        applyEditing: applyProgramEditing,
        validateField: validateProgramField,
        rowChecks: [],
        // 새 행 등록(SWLINK26-46 개선) — POST /program/create ([dto]) 는 생성된 id 배열을 돌려준다
        validateRow: validateProgramRow,
        create: async dto => {
            const res = await createProgram([dto])
            return res?.data?.data?.[0]
        },
        // 그리드 셀만으로 채우기 애매한 값(파일 첨부·강사 매칭 등)은 저장 후 상세 화면에서 추가 입력한다.
        // 여기 기본값은 어디까지나 "빈 칸 시작점"이라, 필수인데 뻔한 값이 없는 항목(제목·기관명·주소 등)은
        // 비워 둬서 검증에 걸리게 하고, 목록에서 고를 수 있는 항목만 첫 옵션으로 미리 채운다.
        newRowDefaults: regionId => ({
            regionId,
            areaName: '',
            status: '예정',
            registrationYear: new Date().getFullYear(),
            educationType: '센터교육',
            course: '비정규과정',
            programName: '',
            category1: PROGRAM_OPTIONS.category1[0],
            category2: '해당없음',
            category3: '해당없음',
            classHours: 0,
            numberOfSession: null,
            numberOfApplicants: 0,
            numberOfGraduates: 0,
            startDate: todayStr(),
            endDate: todayStr(),
            organizationType: '일반학교',
            isUnderserved: false,
            grade: '초등',
            classroom: '',
            organizationName: '',
            address: '',
            managerName: '',
            managerPosition: '',
            managerTelNumber: '',
            managerPhoneNumber: '',
            managerEmail: '',
            managerRemark: '',
        }),
    },
    tutor: {
        label: '강사',
        idField: 'tutorId',
        actionsColId: 'tutorEditActions',
        save: editTutor,
        // 교육실적과 동일한 이유 — 목록에 없는 필드가 전체 갱신에 지워지지 않도록 상세를 바탕으로 깐다
        loadFull: async id => (await getTutorDetail(id))?.data?.data,
        toDTO: mapTutorRowToEditDTO,
        applyEditing: applyTutorEditing,
        validateField: validateTutorField,
        rowChecks: [checkTutorDuplicateName],
        // 새 행 등록(SWLINK26-46 개선) — POST /tutor/create ([dto]) 는 생성된 id 배열을 돌려준다.
        // 자격증 서류 등 첨부가 필요한 부분은 저장 후 상세 화면에서 추가 입력한다.
        validateRow: validateTutorRow,
        create: async dto => {
            const res = await createTutor([dto])
            return res?.data?.data?.[0]
        },
        newRowDefaults: regionId => ({
            regionId,
            registrationYear: new Date().getFullYear(),
            isEmployment: false,
            advancedInstructor: TUTOR_OPTIONS.advancedInstructor[0],
            tutorName: '',
            gender: '남',
            residentNum: '',
            address: '',
            contact: '',
            email: '',
            cohortStartYear: '',
            startDate: todayStr(),
            endDate: todayStr(),
            employmentType: TUTOR_OPTIONS.employmentType[0],
            major: '',
            educationLevel: TUTOR_OPTIONS.educationLevel[0],
            privateCertification: '없음',
            languages: '',
            swCertification: '',
            areaName: '',
            cooperative: '',
            remarks: '',
            isYouth: false,
            isRetiree: false,
            isCareerGapFemale: false,
            isForeign: false,
            isDisability: false,
            isVeterans: false,
            isTeacherCertification: false,
            isTeachingExperience: false,
            isLanguage: false,
            isCar: false,
        }),
    },
}

/** 해당 페이지가 인라인 편집을 지원하면 설정을, 아니면 null 을 반환 (pivot 은 편집 미지원) */
export const getGridEditConfig = (pageType, gridType) =>
    gridType === 'normal' ? GRID_EDIT_CONFIG[pageType] || null : null
